import React from "react";
import "./CyberLogoGlitch.css";

// Cyberpunk's DropWyrm logo glitches like a corrupted holo-ad: every few seconds an SVG filter
// tears it into sideways-shifted bands with hazard-red / acid-yellow channel fringes. Logo <img>s
// opt in with className="brand-logo brand-logo--<sm|md|lg>"; CyberLogoGlitch.css applies the
// filters only under data-genre="cyberpunk". Mounted once by ThemeProvider while that genre is on.

// [keyTime, band shift, channel split], the shifts as fractions of the logo's rendered size.
const IDLE = [[0, 0, 0], [0.62, 0.10, 0.03], [0.64, -0.07, 0.05], [0.66, 0.13, 0.02],
    [0.68, 0.04, 0.04], [0.70, 0, 0], [0.86, -0.09, 0.04], [0.875, 0, 0]];
// Hovered: glitching non-stop.
const HOT = [[0, 0.08, 0.03], [0.2, -0.06, 0.05], [0.4, 0.12, 0.02], [0.6, 0, 0.04], [0.8, -0.1, 0.03]];

// Filter units are CSS px, so each logo size gets its own filter.
const FILTERS = [
    {id: 'dw-glitch-sm', size: 36, dur: '5s', frames: IDLE},     // header
    {id: 'dw-glitch-md', size: 80, dur: '5s', frames: IDLE},     // no-campaign overlay
    {id: 'dw-glitch-lg', size: 170, dur: '5s', frames: IDLE},    // login-type pages
    {id: 'dw-glitch-sm-hot', size: 36, dur: '0.6s', frames: HOT},
    {id: 'dw-glitch-lg-hot', size: 170, dur: '0.6s', frames: HOT},
];

function GlitchFilter({id, size, dur, frames}) {
    const keyTimes = frames.map(f => f[0]).join(';');
    const animate = (attributeName, values) => (
        <animate attributeName={attributeName} values={values.join(';')} keyTimes={keyTimes}
                 dur={dur} calcMode="discrete" repeatCount="indefinite"/>
    );
    const shift = frames.map(f => +(f[1] * size).toFixed(1));
    const split = frames.map(f => +(f[2] * size).toFixed(1));
    return (
        <filter id={id} x="-25%" y="-10%" width="150%" height="120%" colorInterpolationFilters="sRGB">
            {/* No x frequency: noise that only varies down the logo, i.e. horizontal bands. A new
                seed each frame re-cuts them. */}
            <feTurbulence type="fractalNoise" baseFrequency={`0 ${(3.5 / size).toFixed(4)}`}
                          numOctaves="1" seed="3" result="noise">
                {animate('seed', frames.map((f, i) => 3 + i * 7))}
            </feTurbulence>
            {/* Quantised so most bands sit still (0.5 = no shift) and a few jump left or right. */}
            <feComponentTransfer in="noise" result="bands">
                <feFuncR type="discrete" tableValues="0.5 0.5 0.5 0 0.5 0.5 0.5 1 0.5 0.5 0.5 0.3 0.5 0.5"/>
                <feFuncG type="linear" slope="0" intercept="0.5"/>
                <feFuncA type="linear" slope="0" intercept="1"/>
            </feComponentTransfer>
            <feDisplacementMap in="SourceGraphic" in2="bands" scale="0"
                               xChannelSelector="R" yChannelSelector="G" result="torn">
                {animate('scale', shift)}
            </feDisplacementMap>
            {/* Channel split: red and yellow ghosts of the torn logo, pulled apart behind it. */}
            <feColorMatrix in="torn" values="0 0 0 0 1  0 0 0 0 0  0 0 0 0 0.2  0 0 0 0.85 0" result="red"/>
            <feOffset in="red" dx="0" result="redShift">{animate('dx', split.map(v => -v))}</feOffset>
            <feColorMatrix in="torn" values="0 0 0 0 1  0 0 0 0 0.95  0 0 0 0 0.15  0 0 0 0.85 0" result="yellow"/>
            <feOffset in="yellow" dx="0" result="yellowShift">{animate('dx', split)}</feOffset>
            <feMerge>
                <feMergeNode in="redShift"/>
                <feMergeNode in="yellowShift"/>
                <feMergeNode in="torn"/>
            </feMerge>
        </filter>
    );
}

export default function CyberLogoGlitch() {
    return (
        <svg width="0" height="0" aria-hidden="true" focusable="false"
             style={{position: 'absolute', width: 0, height: 0, overflow: 'hidden'}}>
            <defs>{FILTERS.map(f => <GlitchFilter key={f.id} {...f}/>)}</defs>
        </svg>
    );
}
